import {
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";

const GAP = 4;
const MARGIN = 8;

/**
 * A pop-up drawn above the whole page, anchored to the element that opened it (FRONTEND_DESIGN §4.5). A widget,
 * the task panel or any other scroll box clips what is drawn inside it — a "why?" near a widget's right edge was cut
 * off and gave the widget a scrollbar that closed the pop-up when pressed. This one sits below its anchor (above it
 * when there is no room below), moves left to stay inside the window, and follows the anchor when something
 * scrolls. Pass the popover's `panelRef` so a press inside still counts as inside.
 */
export function Floating({
  anchor,
  panelRef,
  align = "start",
  className,
  children,
  ...rest
}: {
  anchor: RefObject<HTMLElement | null>;
  panelRef?: RefObject<HTMLElement | null>;
  /** which edge of the anchor the pop-up lines up with */
  align?: "start" | "center" | "end";
  className?: string;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, "className" | "children">) {
  const box = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      const el = box.current;
      if (!a || !el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      let left = align === "start" ? a.left : align === "end" ? a.right - w : a.left + a.width / 2 - w / 2;
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - w - MARGIN));
      const below = a.bottom + GAP;
      const above = a.top - GAP - h;
      const top = below + h > window.innerHeight - MARGIN && above >= MARGIN ? above : below;
      setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }));
    };
    place();
    let frame = 0;
    const later = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    // capture: a scroll in any box (the widget, the page column) moves the anchor
    window.addEventListener("scroll", later, true);
    window.addEventListener("resize", later);
    const obs = new ResizeObserver(later);
    if (box.current) obs.observe(box.current);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", later, true);
      window.removeEventListener("resize", later);
      obs.disconnect();
    };
  }, [anchor, align]);

  return createPortal(
    <div
      {...rest}
      ref={(el) => {
        box.current = el;
        if (panelRef) panelRef.current = el;
      }}
      // measured before it is shown: no flash in the wrong place
      style={{
        position: "fixed",
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
      }}
      className={clsx("z-50", className)}
    >
      {children}
    </div>,
    document.body,
  );
}
